import {useParams} from 'react-router-dom';
import {Alert, Stack} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {FormPageLayout, PlatformLoader} from '@flexinet/ui-components';

import {Profile} from '../profile/Profile.tsx';
import {EditPageNavigation} from '../navigation/EditPageNavigation.tsx';
import {useReconciliationReport} from '../hooks/useReconciliationReport.ts';
import {moduleConfig} from '../config';
import {reportTitle} from '../utils/reportTitle.ts';

// FNP-535: read-only detail view of a single ChannelReconciler (Job B) run. No edit
// drawer / tools — the reconciler owns the lifecycle. Renders the run summary, the
// set-compare counts, and the top-N discrepancy details.
export const DetailPage = () => {
    const {id: reportId} = useParams();

    if (typeof reportId === 'undefined') {
        throw new Error('reportId is required parameter');
    }

    const {
        record: report,
        loading,
        isError,
        notFound,
    } = useReconciliationReport(reportId);

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading reconciliation report...Please wait')}/>;
    }

    // Never hang on the loader, and never call a failed load "not found" (§13): they
    // send an operator in opposite directions.
    if (isError) {
        return (
            <Alert color='red' title={I18n.get('Could not load the reconciliation report')}>
                {I18n.get('The billing API did not return report')} <code>{reportId}</code>.{' '}
                {I18n.get('Reload the page; if it persists, the API may be unavailable in this environment.')}
            </Alert>
        );
    }

    if (notFound || !report) {
        return (
            <Alert color='yellow' title={I18n.get('Reconciliation report not found')}>
                {I18n.get('No reconciliation report exists with id')} <code>{reportId}</code>.
            </Alert>
        );
    }

    return (
        <FormPageLayout
            title={`${I18n.get(moduleConfig.titles.singular)}: ${reportTitle(report)}`}
            pageNavigation={<EditPageNavigation component={report}/>}
        >
            <Stack gap='lg'>
                <Profile record={report}/>
            </Stack>
        </FormPageLayout>
    );
};
